import React, { useState } from 'react'
import { Alert, Box, Button, Chip, Grid, Typography } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import Loader from '../../../common/Loader'
import CopyIcon from '../../../common/CopyIcon'
import { FormProvider, RHFDateRangePickerField, RHFTextField } from '../../../common/hook-form'
import { useGetArchivedChannelOrdersQuery } from '../../../redux/commonRTK'
import { getMerchant } from '../../../helpers/ReduxHelpers'
import { ChannelIcon, formatCurrency, formatDate } from '../../../helpers/UtilityHelper'
import { filterBarSx, primaryBtnSx, secondaryBtnSx, btnRowSx } from '../../../styles/patterns'
import { primaryTextSx, secondaryTextSx, cellStackSx, getPaymentChipSx } from './cellStyles'
import { WEIGHT_FILTER_DEFAULTS, WeightRangeFields, buildWeightFilters } from './weightFilter'
import { FONT } from '../../../styles/foundation/typography'

// Archived orders are read-only: no selection and no row actions. The backend search is scoped to the
// merchant and wraps each order, so every field here is prefixed with `channelOrder.` (archive metadata
// such as `archivedAt` sits beside it) and the RTK query flattens the rows back to plain orders.
const ORDER_FIELD = (name) => `channelOrder.${name}`

export default function ArchivedChannelOrders() {
  const { enqueueSnackbar } = useSnackbar()
  const merchant = getMerchant()
  const merchantId = merchant?.id || ''

  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const [andFilters, setAndFilters] = useState([])

  const methods = useForm({
    defaultValues: { order_number: '', fromDate: '', toDate: '', ...WEIGHT_FILTER_DEFAULTS },
  })
  const { handleSubmit, reset } = methods

  const { data, isLoading, isFetching } = useGetArchivedChannelOrdersQuery({
    andfilter: [{ field: ORDER_FIELD('merchant.$id'), operator: 'eq', value: merchantId }, ...andFilters],
    sort: { field: 'archivedAt', direction: 'DESC' },
    offset: page * sizePerPage,
    limit: sizePerPage,
  })

  const applyFilters = (filters) => {
    const weight = buildWeightFilters(filters)
    if (weight.error) {
      enqueueSnackbar(weight.error, { variant: 'error' })
      return
    }
    const applied = weight.filters.map((f) => ({ ...f, field: ORDER_FIELD(f.field) }))
    if (filters.order_number) {
      applied.push({ field: ORDER_FIELD('order_number'), operator: 'in', value: filters.order_number.split(',').map((v) => v.trim()).filter(Boolean) })
    }
    // Date range is the day the order was archived (not the order date).
    if (filters.fromDate) {
      applied.push({ field: 'archivedAt', operator: 'gte', value: `Date('${formatDate(filters.fromDate, 'yyyy-MM-dd')} 00:00:00')` })
    }
    if (filters.toDate) {
      applied.push({ field: 'archivedAt', operator: 'lte', value: `Date('${formatDate(filters.toDate, 'yyyy-MM-dd')} 23:59:59')` })
    }
    setPage(0)
    setAndFilters(applied)
  }

  const columns = [
    {
      field: 'channel', sortable: false, flex: 1.2, minWidth: 160, headerAlign: 'left', hideSortIcons: true, headerName: 'Order No',
      renderCell: ({ row }) => (
        <Box sx={cellStackSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <Typography sx={primaryTextSx}>{row.order_number}</Typography>
            {row.order_number && <CopyIcon text={String(row.order_number)} title="Copy Order No" />}
          </Box>
          <ChannelIcon name={row.channel} />
        </Box>
      ),
    },
    {
      field: 'store_name', flex: 1, minWidth: 120, sortable: false, headerAlign: 'left', hideSortIcons: true, headerName: 'Store Name',
      renderCell: ({ row }) => (
        <Box sx={cellStackSx}><Typography sx={primaryTextSx}>{row.store_name}</Typography></Box>
      ),
    },
    {
      field: 'createdDate', flex: 0.9, minWidth: 110, sortable: false, headerAlign: 'left', hideSortIcons: true, headerName: 'Order Date',
      renderCell: ({ row }) => (
        <Box sx={cellStackSx}>
          <Typography sx={primaryTextSx}>{formatDate(row.createdDate, 'dd MMM, yyyy')}</Typography>
          <Typography sx={secondaryTextSx}>{formatDate(row.createdDate, 'hh:mm aaa')}</Typography>
        </Box>
      ),
    },
    {
      field: 'customer', flex: 1.2, minWidth: 150, sortable: false, headerAlign: 'left', hideSortIcons: true, headerName: 'Customer Details',
      // The backend already masks email/phone for users without PII access.
      renderCell: ({ row }) => (
        <Box sx={cellStackSx}>
          <Typography sx={primaryTextSx}>{row.customer?.first_name}</Typography>
          <Typography sx={secondaryTextSx}>{row.customer?.email}</Typography>
          <Typography sx={secondaryTextSx}>{row.customer?.phone}</Typography>
        </Box>
      ),
    },
    {
      field: 'items', flex: 1.3, minWidth: 170, sortable: false, headerAlign: 'left', hideSortIcons: true, headerName: 'Items',
      renderCell: ({ row }) => {
        const items = row.orderItems || []
        return (
          <Box sx={cellStackSx}>
            {items.slice(0, 2).map((item, i) => (
              <Typography key={i} sx={secondaryTextSx}>{item.title || item.skuId}{item.units ? ` × ${item.units}` : ''}</Typography>
            ))}
            {items.length > 2 && <Typography sx={secondaryTextSx}>+{items.length - 2} more</Typography>}
          </Box>
        )
      },
    },
    {
      field: 'paymentDetails', flex: 0.9, minWidth: 110, sortable: false, headerAlign: 'left', hideSortIcons: true, headerName: 'Payment',
      renderCell: ({ row }) => (
        <Box sx={cellStackSx}>
          {row.total_price && <Typography sx={primaryTextSx}>{formatCurrency(row.total_price)}</Typography>}
          {row.payment_mode && <Chip size="small" variant="outlined" label={row.payment_mode} sx={getPaymentChipSx(row.payment_mode)} />}
        </Box>
      ),
    },
    {
      field: 'mesurements', flex: 0.6, minWidth: 80, sortable: false, headerAlign: 'left', hideSortIcons: true, headerName: 'Weight',
      renderCell: ({ row }) => (
        <Box sx={cellStackSx}><Typography sx={primaryTextSx}>{row.total_weight?.toFixed(2)} gm</Typography></Box>
      ),
    },
    {
      field: 'archivedAt', flex: 1.1, minWidth: 140, sortable: false, headerAlign: 'left', hideSortIcons: true, headerName: 'Archived On',
      renderCell: ({ row }) => (
        <Box sx={cellStackSx}>
          <Typography sx={primaryTextSx}>{row.archivedAt ? formatDate(row.archivedAt, 'dd MMM, yyyy') : '-'}</Typography>
          <Typography sx={secondaryTextSx}>
            {row.source === 'CRON' ? 'Auto-archived' : row.archivedBy ? `by ${row.archivedBy}` : ''}
          </Typography>
        </Box>
      ),
    },
  ]

  return (
    <div>
      {(isLoading || isFetching) && <Loader />}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Alert severity="info" sx={{ fontFamily: FONT }}>
            Archived orders are read-only and are removed permanently after the retention period.
          </Alert>
        </Grid>
        <Grid item xs={12}>
          <Box data-rp-filters sx={filterBarSx}>
            <FormProvider methods={methods} onSubmit={handleSubmit(applyFilters)}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <RHFTextField name="order_number" label="Order Number (Comma Separated)" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <RHFDateRangePickerField label="Archived Date" maxDate={new Date()} />
                </Grid>
                <WeightRangeFields />
                <Grid item xs={12}>
                  <Box sx={btnRowSx}>
                    <Button type="submit" variant="contained" sx={primaryBtnSx}>Filter</Button>
                    <Button variant="outlined" sx={secondaryBtnSx} onClick={() => {
                      reset()
                      setPage(0)
                      setAndFilters([])
                    }}>Reset</Button>
                  </Box>
                </Grid>
              </Grid>
            </FormProvider>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <ProzoDataGrid
            columns={columns}
            rows={data?.data || []}
            sizePerPage={sizePerPage}
            setSizePerPage={setSizePerPage}
            setPage={setPage}
            page={page}
            pagination={true}
            hideFooterPagination={false}
            filterMode={'server'}
            rowCount={data?.count}
            getRowId={(row) => row?.channelOrderId}
          />
        </Grid>
      </Grid>
    </div>
  )
}
